import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutDashboard,
  FileUp,
  ClipboardList,
  FileText,
  TableProperties,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  MessageSquare,
} from 'lucide-react';
import { logout } from '../store/authSlice';
import { setSelectedTaxYear } from '../store/analysisSlice';
import { setActiveProject } from '../store/projectsSlice';
import { RootState } from '../store';
import { authApi } from '../services/api';
import FeedbackModal from './FeedbackModal';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const selectedTaxYear = useSelector((state: RootState) => state.analysis.selectedTaxYear);
  const { projects, activeProjectId } = useSelector((state: RootState) => state.projects);

  const user = useSelector((state: RootState) => state.auth.user);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token') || undefined;
      await authApi.logout(refreshToken);
    } catch {
      // Local logout still matters even if the network request fails.
    }
    dispatch(logout());
    navigate('/login');
  };

  const navItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard'
    },
    {
      label: 'Upload Documents',
      icon: FileUp,
      path: '/upload'
    },
    {
      label: 'Questionnaire',
      icon: ClipboardList,
      path: '/questionnaire'
    },
    {
      label: 'Tax Review',
      icon: FileText,
      path: '/review'
    },
    {
      label: 'Tax Inputs',
      icon: TableProperties,
      path: '/inputs'
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/settings'
    }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Left: Logo and Mobile Menu */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:block text-gray-600 hover:text-gray-900"
            >
              {sidebarOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
            <img src="/branding/filie-logo-full.svg" alt="Filie" className="h-7" />
          </div>

          {/* Right: Tax Year Selector and User Menu */}
          <div className="flex items-center space-x-6">
            {/* Tax Year Selector */}
            <div className="hidden sm:block">
              <select
                value={selectedTaxYear}
                onChange={(e) => dispatch(setSelectedTaxYear(Number(e.target.value)))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={2025}>Tax Year 2025</option>
                <option value={2024}>Tax Year 2024</option>
                <option value={2023}>Tax Year 2023</option>
                <option value={2022}>Tax Year 2022</option>
                <option value={2021}>Tax Year 2021</option>
                <option value={2020}>Tax Year 2020</option>
              </select>
            </div>

            <button
              onClick={() => setFeedbackOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
            >
              <MessageSquare className="h-4 w-4" />
              <span>Report issue</span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center text-white font-medium text-sm">
                  {user?.firstName?.charAt(0) || 'U'}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 border-t border-gray-200"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-1 max-h-96 overflow-y-auto">
              <button
                onClick={() => {
                  setFeedbackOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50"
              >
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">Report Issue</span>
              </button>
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                      isActive(item.path)
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 overflow-y-auto">
            {/* Project Selector */}
            {projects.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1.5">Project</p>
                <select
                  value={activeProjectId || ''}
                  onChange={(e) => dispatch(setActiveProject(Number(e.target.value)))}
                  className="w-full text-sm font-medium text-gray-900 border border-gray-200 rounded-lg px-3 py-2 bg-white"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.taxYear})</option>
                  ))}
                </select>
              </div>
            )}
            <nav className="p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                      isActive(item.path)
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Sidebar Footer */}
            <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4 bg-white w-64">
              <button
                onClick={() => setFeedbackOpen(true)}
                className="mb-2 w-full flex items-center space-x-3 px-4 py-2.5 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-50 transition-colors text-sm"
              >
                <MessageSquare className="w-5 h-5" />
                <span>Report Issue</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-2.5 text-gray-400 rounded-lg hover:text-red-600 hover:bg-red-50 transition-colors text-sm"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-8">{children}</div>
        </main>
      </div>

      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        page={location.pathname}
        taxYear={selectedTaxYear}
      />
    </div>
  );
}
